import { Box, Button, Chip, Modal, Switch } from '@mui/material'
import ButtonPrimary from 'components/ButtonPrimary/ButtonPrimary'
import { Colors } from 'constants/colors'
import { useGetSubscriptionQuery } from 'graphql/generated/graphql'
import React from 'react'
import SubCardStyle from './Style'
import { ISubscriptionCard, TFeatures } from './Types'
import { useUser } from 'hooks/useUser'

export default function SubscriptionCard(
  props: ISubscriptionCard
): JSX.Element {
  const { data: subsData, refetch: refetchSubs } = useGetSubscriptionQuery()
  const { user, refetch: refetchUser } = useUser()
  const [openModal, setOpenModal] = React.useState(false)

  // Check if this is the user's current plan
  const currentSubscription = user?.subscription
  const isCurrentPlan = currentSubscription?.planName === props.plan
  const isFreePlan = currentSubscription?.status === 'FREE' || !currentSubscription
  const isCanceled = currentSubscription?.status === 'CANCELED'
  const isExpired = currentSubscription?.status === 'EXPIRED'
  const isActive = currentSubscription?.status === 'ACTIVE'

  // Determine button state
  const getButtonTitle = () => {
    if (isCurrentPlan && isActive) return 'Current Plan'
    if (isCurrentPlan && isCanceled) return 'Reactivate'
    if (isCurrentPlan && isExpired) return 'Resubscribe'
    if (isFreePlan || isExpired) return 'Get Started'
    return 'Switch Plan'
  }

  const isButtonDisabled = isCurrentPlan && isActive

  const handleSubscribe = async () => {
    if (subsData?.getSubscription?.stripeSubscriptionId && !isCurrentPlan && !isCanceled && !isExpired) {
      setOpenModal(true)
      return
    }
  
    const priceId = props.switchValue 
      ? props.stripePriceIds.yearly 
      : props.stripePriceIds.monthly
  
    props.setIsLoading(true)
    try {
      const response = await fetch('/payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: priceId, // Changed from lookup_key
        }),
      })
      const result = await response.json()
      if (result.error) {
        alert(result.error)
        props.setIsLoading(false)
      } else {
        window.location.href = result.url
      }
    } catch (e) {
      console.log(e)
      props.setIsLoading(false)
    }
  }
  
  const handlePlanSwitch = async () => {
    props.setIsLoading(true)
    const priceId = props.switchValue 
      ? props.stripePriceIds.yearly 
      : props.stripePriceIds.monthly
  
    try {
      const response = await fetch('/upgrade-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: priceId, // Changed from lookup_key
          subscription_id: subsData?.getSubscription?.stripeSubscriptionId,
          customer_id: subsData?.getSubscription?.customerId,
          planName: props.plan,
        }),
      })
      const result = await response.json()
      if (result.success) {
        alert('You have successfully changed your plan!')
        await refetchSubs()
        await refetchUser()
      } else {
        alert(result.error || 'Failed to switch plan')
      }
      props.setIsLoading(false)
      setOpenModal(false)
    } catch (e) {
      console.log(e)
      alert('An error occurred while switching plans')
      props.setIsLoading(false)
      setOpenModal(false)
    }
  }

  return (
    <SubCardStyle.CardWrapper elevation={3} sx={{ position: 'relative' }}>
      {/* Current Plan Badge */}
      {isCurrentPlan && isActive && (
        <Chip
          label="Current Plan"
          color="primary"
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        />
      )}
      
      {isCurrentPlan && isCanceled && (
        <Chip
          label="Canceled"
          color="warning"
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        />
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <SubCardStyle.CardTitle>{props.cardTitle}</SubCardStyle.CardTitle>
        <div>
          <Switch
            checked={props.switchValue}
            onChange={(e) => {
              props.setSwitchValue(e.target.checked)
            }}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#1d64f2',
                '&:hover': {
                  backgroundColor: 'rgba(29, 100, 242, 0.08)',
                },
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#1d64f2',
              },
              '& .MuiSwitch-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: '#ffffff',
              },
            }}
          />
          <span
            style={props.switchValue ? { color: '#2196f3' } : { color: 'gray' }}
          >
            {props.switchValue ? 'YEARLY' : 'MONTHLY'}
          </span>
        </div>
      </div>

      <SubCardStyle.PricingContainer>
        <SubCardStyle.SymbolStyle>$ </SubCardStyle.SymbolStyle>
        <SubCardStyle.CardPricing>
          {props.switchValue ? props.pricing.yearly : props.pricing.monthly}
        </SubCardStyle.CardPricing>
        <SubCardStyle.PricingText>/month</SubCardStyle.PricingText>
      </SubCardStyle.PricingContainer>

      {props.switchValue && (
        <div style={{ fontSize: '12px', color: '#666', marginTop: '-10px' }}>
          Billed ${parseInt(props.pricing.yearly) * 12} yearly
        </div>
      )}

      <SubCardStyle.CardSubtitle>
        {props.planDescription}
      </SubCardStyle.CardSubtitle>

      <SubCardStyle.CardDetailsWrapper>
        {props.features?.map((item: TFeatures) => (
          <SubCardStyle.CardDetails key={item.id}>
            <item.icon
              style={{
                fontSize: '20px',
                marginRight: '15px',
                color: Colors.green,
              }}
            />
            {item.title}
          </SubCardStyle.CardDetails>
        ))}
      </SubCardStyle.CardDetailsWrapper>

      <ButtonPrimary
          onClick={() => {
            handleSubscribe().catch(console.error)
          }}
        width="100%"
        buttonTitle={getButtonTitle()}
        variant={isCurrentPlan && isActive ? 'outlined' : 'contained'}
        disabled={isButtonDisabled}
      />

      {/* Switch Plan Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            padding: '20px 40px',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <h2 style={{ textAlign: 'center' }}>
            Switch to {props.cardTitle} plan?
          </h2>
          <p style={{ textAlign: 'center', color: '#666' }}>
            Your current subscription will be prorated and you&apos;ll be charged the difference.
          </p>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flex: 1,
              width: '100%',
              gap: '5px',
              marginTop: '10px',
            }}
          >
            <Button
              fullWidth
              color="error"
              variant="contained"
              onClick={() => setOpenModal(false)}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              color="success"
              variant="contained"
              onClick={() => {
                handlePlanSwitch().catch(console.error)
              }}
            >
              Confirm Switch
            </Button>
          </Box>
        </Box>
      </Modal>
    </SubCardStyle.CardWrapper>
  )
}